import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Dropdown, Flex, message, Popconfirm, Empty } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import FileRowHeader from './fileRowHeader';
import { BuildingHeader, BuildingItem, BuildingList, Buildings, BuildingsInner, FloorListCard, FloorListCardBody, FloorListCardHead, FloorManageWrapper, Floors, FloorsInner, HiddenUpload, RowHeader, SplitDivider, } from './Floor.style';
import Input from '../../components/common/Input';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import DraggableUploadContainer from './draggableUploadContainer';
import { createFloorAction, uploadFloorImageAction, createBuildingAction } from '../../store/floorSlice';
import { _changeBuildingName, _changeDefaultBuildingFloor, _deleteBuilding } from '../../shared/httpservice/apis';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
const FloorList = ({ buildingInfo, fetchBuildingsAndFloors }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const siteId = useSelector((state) => state?.site?.checkSite?.response?.data?.siteId);
    const [activeBuilding, setActiveBuilding] = useState();
    const [buildingName, setBuildingName] = useState(null);
    const [buildingClicked, setBuildingClicked] = useState(false);
    const [buildingIndex, setBuildingIndex] = useState(0);
    const [buildingCreated, setBuildingCreated] = useState(false);
    const [buildingDeleted, setBuildingDeleted] = useState(false);
    const [alignClicked, setAlignClicked] = useState(false);
    const [isFloorName, setIsFloorName] = useState(false);
    const [isBuildingName, setIsBuildingName] = useState(false);
    const [floorLoading, setFloorLoading] = useState(false);
    const buildingParams = `buildingId=${activeBuilding?.buildingId}`;
    useEffect(() => {
        if (activeBuilding?.name) {
            setBuildingName(activeBuilding?.name);
        }
    }, [activeBuilding]);
    useEffect(() => {
        if (activeBuilding) {
            const hasEmptyShortName = activeBuilding?.floors.some((floor) => floor?.shortName === '');
            const hasBuildingName = activeBuilding?.name === '';
            setIsFloorName(!hasEmptyShortName);
            setIsBuildingName(!hasBuildingName);
        }
    }, [activeBuilding]);
    useEffect(() => {
        if (buildingInfo && buildingInfo?.length && !buildingDeleted) {
            if (!buildingClicked) {
                setActiveBuilding(buildingInfo[0]);
            }
            else {
                if (buildingInfo[buildingIndex]) {
                    setActiveBuilding(buildingInfo[buildingIndex]);
                }
            }
        }
    }, [buildingInfo, buildingIndex]);
    const [aboveGroundFloors, setAboveGroundFloors] = useState([]);
    const [belowGroundFloors, setBelowGroundFloors] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (activeBuilding) {
            const aboveFloors = activeBuilding?.floors?.filter((floor) => floor?.elevation >= 0);
            const belowFloors = activeBuilding?.floors?.filter((floor) => floor?.elevation < 0);
            setAboveGroundFloors(aboveFloors);
            setBelowGroundFloors(belowFloors);
            setBuildingName(activeBuilding?.name || '');
        }
    }, [activeBuilding]);
    const [groundType, setGroundType] = useState('above'); // Track ground type for elevation
    const handleMenuClick = (e) => {
        const selectedGroundType = e.key === '1' ? 'above' : 'below';
        setGroundType(selectedGroundType);
        message.info(`Selected ${selectedGroundType === 'above' ? 'Above Ground' : 'Below Ground'}`);
        // Trigger the file upload
        document.getElementById('file-upload-button')?.click(); // Trigger click on the hidden file input
    };
    const items = [
        {
            key: '1',
            label: 'Above Ground',
        },
        {
            key: '2',
            label: 'Below Ground',
        },
    ];
    const menuProps = {
        items,
        onClick: handleMenuClick,
    };
    // Convert file to Base64 and remove prefix
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file); // Read file as a Base64 string
            reader.onload = () => {
                const imageString = reader.result;
                const base64Data = imageString.replace(/^data:image\/[a-z]+;base64,/, ''); // Remove the base64 prefix
                resolve(base64Data); // Return the cleaned base64 string
            };
            reader.onerror = (error) => reject(error);
        });
    };
    // Extract the dimensions of the image (width and height)
    const getImageDimensions = (base64String) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = `data:image/jpeg;base64,${base64String}`; // Set the Base64 string as the image source
            img.onload = () => resolve({
                imageWidth: img.naturalWidth,
                imageHeight: img.naturalHeight,
            });
            img.onerror = (error) => reject(error);
        });
    };
    const handleFileUpload = async (file) => {
        setFloorLoading(true);
        try {
            if (siteId) {
                const elevation = groundType === 'above'
                    ? aboveGroundFloors.length // Above ground
                    : -(belowGroundFloors.length + 1); // Below ground
                const base64String = await convertFileToBase64(file);
                // Get the dimensions of the image
                const { imageWidth, imageHeight } = await getImageDimensions(base64String);
                const floorResponse = await dispatch(createFloorAction({
                    name: file.name,
                    shortName: `Floor ${elevation}`,
                    elevation: elevation,
                    width: imageWidth,
                    height: imageHeight,
                    siteId: siteId,
                    buildingId: activeBuilding?.buildingId,
                }));
                const floorId = floorResponse?.payload?.data?.floorId;
                if (activeBuilding && activeBuilding?.floors?.length === 0) {
                    await _changeDefaultBuildingFloor(activeBuilding?.buildingId, siteId, floorId);
                }
                await dispatch(uploadFloorImageAction({
                    siteId,
                    floorId,
                    data: { image: base64String },
                }));
                await fetchBuildingsAndFloors();
                message.success('Floor added succesfully!');
                setFloorLoading(false);
            }
        }
        catch (error) {
            message.error('Upload failed.');
            setFloorLoading(false);
        }
    };
    const handleContinue = async () => {
        if (!siteId) {
            message.error('Site ID is required to create a building.');
            return;
        }
        setLoading(true);
        try {
            await dispatch(createBuildingAction({ name: 'New Building', siteId }));
            await fetchBuildingsAndFloors();
            message.success('Building created successfully.');
            setBuildingCreated(true);
        }
        catch (error) {
            message.error('Failed to create building.');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (buildingCreated && buildingInfo?.length) {
            setActiveBuilding(buildingInfo[buildingInfo?.length - 1]);
            setBuildingCreated(false);
        }
    }, [buildingCreated]);
    useEffect(() => {
        if (buildingDeleted && buildingInfo?.length) {
            setActiveBuilding(buildingInfo[0]);
            setBuildingDeleted(false);
        }
    }, [buildingDeleted]);
    const handleClickBuilding = (id) => {
        if (buildingInfo) {
            setBuildingClicked(true);
            const selectedBuilding = buildingInfo?.find((item) => item?.buildingId === id);
            const index = buildingInfo?.findIndex((building) => building?.buildingId === id);
            setBuildingIndex(index);
            setActiveBuilding(selectedBuilding);
        }
    };
    const handleDeleteBuilding = async (buildingId) => {
        try {
            if (siteId) {
                await _deleteBuilding(buildingId, siteId);
                fetchBuildingsAndFloors();
            }
        }
        catch {
            message.error('Failed to delete building.');
        }
    };
    const handleBlur = async () => {
        if (siteId && buildingName !== activeBuilding?.name) {
            try {
                //@ts-ignore
                await _changeBuildingName(activeBuilding?.buildingId, siteId, buildingName);
                fetchBuildingsAndFloors();
            }
            catch {
                message.error('Failed to update building name.');
            }
        }
    };
    return (_jsx(_Fragment, { children: _jsxs(FloorManageWrapper, { children: [_jsx(Buildings, { children: _jsxs(BuildingsInner, { children: [_jsx(BuildingHeader, { children: _jsxs(RowHeader, { children: [_jsx("h3", { children: "Buildings" }), _jsx(Button, { color: "primary", variant: "solid", size: "small", onClick: () => {
                                                handleContinue();
                                            }, children: "Create Building" })] }) }), _jsx(BuildingList, { itemLayout: "horizontal", dataSource: buildingInfo && buildingInfo?.length && buildingInfo, renderItem: (item, index) => (_jsxs(BuildingItem, { Selected: item?.buildingId === activeBuilding?.buildingId, onClick: () => {
                                        handleClickBuilding(item?.buildingId);
                                    }, actions: [
                                        item?.floors?.length === 0 ? (_jsx(Dropdown, { trigger: ['click'], arrow: true, menu: {
                                                items: [
                                                    {
                                                        label: (_jsx(Popconfirm, { title: "Delete the building?", description: "Are you sure to delete this building?", icon: _jsx(QuestionCircleOutlined, { style: { color: 'red' } }), onConfirm: () => {
                                                                handleDeleteBuilding(item?.buildingId);
                                                            }, okText: "Delete", cancelText: "Cancel", children: _jsx("span", { children: "Delete building" }) })),
                                                        key: 'Delete',
                                                        danger: true,
                                                        icon: (_jsx(Popconfirm, { title: "Delete the building?", description: "Are you sure to delete this building?", icon: _jsx(QuestionCircleOutlined, { style: { color: 'red' } }), onConfirm: () => {
                                                                handleDeleteBuilding(item?.buildingId);
                                                            }, okText: "Delete", cancelText: "Cancel", children: _jsx(Icon, { icon: "trash-03", size: 18 }) })),
                                                    },
                                                ],
                                            }, children: _jsx(Icon, { icon: "dots-vertical", size: 18 }) })) : null,
                                    ], children: [_jsx("span", { className: "ic-sec", children: _jsx(Icon, { icon: "drag", size: 16, style: {
                                                    cursor: 'grab',
                                                } }) }), _jsxs("h5", { children: [item?.name, item?.floors?.length ? _jsxs("span", { children: ["Floors: ", item?.floors?.length] }) : null] })] })) })] }) }), _jsx(Floors, { children: _jsx(FloorsInner, { children: _jsxs(FloorListCard, { children: [_jsx(FloorListCardHead, { children: _jsxs(Flex, { align: "center", justify: "flex-start", gap: 'large', children: [_jsx("label", { children: "Building Info" }), _jsx(Input, { maxLength: 20, WrapperStyle: { flex: 1 }, variant: "filled", name: "building_info", placeholder: "Name", value: typeof buildingName === 'string' ? buildingName : '', onChange: (e) => {
                                                    setBuildingName(e.target.value);
                                                }, onBlur: handleBlur, error: buildingName === '' ? 'Building name is required' : false })] }) }), _jsxs(RowHeader, { children: [_jsx("h3", { children: "Floors" }), _jsxs(Flex, { align: "center", gap: 'small', justify: "flex-end", children: [_jsx(Button, { onClick: () => {
                                                        if (activeBuilding && activeBuilding?.floors?.length) {
                                                            if (isFloorName && isBuildingName) {
                                                                navigate({
                                                                    pathname: '/align',
                                                                    search: buildingParams,
                                                                });
                                                            }
                                                            setAlignClicked(true);
                                                        }
                                                        else {
                                                            message.error("Add some floors to the building");
                                                        }
                                                    }, variant: "solid", color: "gray", children: "Align Floors" }), _jsx(Dropdown, { menu: menuProps, children: _jsx(Button, { variant: "solid", color: "primary", icon: _jsx(Icon, { icon: "upload-cloud-01", size: 16 }), children: "Add New Floor" }) })] })] }), _jsxs(FloorListCardBody, { children: [_jsx(FileRowHeader, {}), _jsx(HiddenUpload, { accept: ".png,.jpg,.jpeg", beforeUpload: (file) => {
                                                const isAllowedType = ['image/png', 'image/jpeg'].includes(file.type);
                                                if (!isAllowedType) {
                                                    message.error(`${file.name} is not a supported file type.`);
                                                }
                                                return isAllowedType;
                                            }, customRequest: (e) => handleFileUpload(e.file), showUploadList: false, id: "file-upload-button", style: { display: 'none' }, children: _jsx("span", { children: "Upload File" }) }), floorLoading ? (_jsx("div", { children: "Loading...." })) : (_jsx(FloorListCardHead, { style: { paddingInline: 0 }, children: activeBuilding && activeBuilding?.floors?.length === 0 ? (_jsx(Empty, { description: "Add new floors" })) : (_jsxs(_Fragment, { children: [aboveGroundFloors?.length ? (_jsx(DraggableUploadContainer, { floors: aboveGroundFloors, fetchBuildingsAndFloors: fetchBuildingsAndFloors, defaultFloorId: activeBuilding?.defaultFloorId, alignClicked: alignClicked })) : null, belowGroundFloors?.length ? (_jsxs(_Fragment, { children: [_jsx(SplitDivider, { variant: "dashed", children: "Below Ground Level" }), _jsx(DraggableUploadContainer, { floors: belowGroundFloors, fetchBuildingsAndFloors: fetchBuildingsAndFloors, defaultFloorId: activeBuilding?.defaultFloorId, alignClicked: alignClicked })] })) : null] })) }))] })] }) }) })] }) }));
};
export default FloorList;
